// Enhanced download functionality for FileZen - REAL ENHANCEMENT
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { marked } from 'marked';
import { Document, Packer, Paragraph, TextRun, AlignmentType, BorderStyle, Table, TableRow, TableCell, WidthType, VerticalAlign, TableLayoutType } from 'docx';

export const downloadEnhancedFile = async (result, format) => {
  try {
    console.log('Download called with format:', format, 'result:', result);

    // Get the enhancement suggestions or analysis from result
    const enhancementText = result.result?.enhancement_suggestions || result.result?.analysis || '';
    // First try to get the formatted data section
    const formattedDataMatch = enhancementText.match(/(?:# FORMATTED DATA|# IMPROVED DATA|# ENHANCED DATA)([\s\S]+?)(?:\n#|\n\n#|$)/i);
    let formattedData = formattedDataMatch ? formattedDataMatch[1].trim() : null;

    // If no formatted data section found, use the entire enhancement text
    if (!formattedData) {
      formattedData = enhancementText;
    }

    // Determine the appropriate format based on job type if not specified
    let downloadFormat = format;
    if (!downloadFormat) {
      if (result.jobType?.includes('document') || result.result?.file_type === 'docx' || result.result?.file_type === 'txt') {
        downloadFormat = 'docx';
      } else if (result.jobType?.startsWith('excel_') || result.result?.file_type === 'xlsx') {
        downloadFormat = 'xlsx';
      } else if (result.result?.file_type === 'csv') {
        downloadFormat = 'csv';
      } else if (result.result?.file_type === 'pdf') {
        downloadFormat = 'pdf';
      } else {
        downloadFormat = 'txt';
      }
    }

    // Override format based on job type to ensure correct file type
    if (result.jobType?.includes('document') && downloadFormat === 'xlsx') {
      console.log('Overriding format from xlsx to docx based on job type');
      downloadFormat = 'docx';
    } else if (result.jobType?.startsWith('excel_') && downloadFormat === 'docx') {
      console.log('Overriding format from docx to xlsx based on job type');
      downloadFormat = 'xlsx';
    }

    console.log('Using download format:', downloadFormat);

    // Generate a professional file name based on content and job type
    let fileName = "";

    // First, try to extract a meaningful title from the content
    if (result.result?.enhancement_suggestions) {
      const content = result.result.enhancement_suggestions;

      // Look for main title (H1 heading) - most reliable
      const h1Match = content.match(/^#\s+(.+?)(?:\n|$)/m);
      if (h1Match && h1Match[1]) {
        let title = h1Match[1].trim();
        // Clean the title for filename use
        title = title
          .replace(/[^\w\s-]/g, '') // Remove special characters except spaces and hyphens
          .replace(/\s+/g, ' ')     // Normalize spaces
          .trim();

        if (title.length > 3 && title.length < 60) {
          fileName = title;
        }
      }

      // If no H1 title found, look for H2 headings
      if (!fileName) {
        const h2Match = content.match(/^##\s+(.+?)(?:\n|$)/m);
        if (h2Match && h2Match[1]) {
          let title = h2Match[1].trim();
          title = title
            .replace(/[^\w\s-]/g, '')
            .replace(/\s+/g, ' ')
            .trim();

          if (title.length > 3 && title.length < 60) {
            fileName = title;
          }
        }
      }

      // If still no title, look for the first meaningful line
      if (!fileName) {
        const lines = content.split('\n');
        for (const line of lines) {
          const trimmedLine = line.trim();
          // Skip empty lines, markdown formatting, and very short lines
          if (trimmedLine &&
            !trimmedLine.startsWith('#') &&
            !trimmedLine.startsWith('*') &&
            !trimmedLine.startsWith('-') &&
            !trimmedLine.startsWith('•') &&
            trimmedLine.length > 10 &&
            trimmedLine.length < 80 &&
            /^[A-Z]/.test(trimmedLine)) { // Starts with capital letter

            let title = trimmedLine
              .replace(/[^\w\s-]/g, '')
              .replace(/\s+/g, ' ')
              .trim();

            if (title.length > 5 && title.length < 60) {
              fileName = title;
              break;
            }
          }
        }
      }

      // If still no title, look for common document patterns
      if (!fileName) {
        const titlePatterns = [
          /(?:title|report|analysis|dashboard|summary|data|document|plan|strategy)(?:\s+for|:)?\s+([A-Za-z0-9\s]+?)(?:\n|\.|,|$)/i,
          /^([A-Z][A-Za-z0-9\s]{5,50})(?:\n|$)/m,
          /(?:^|\n)([A-Z][A-Za-z0-9\s]{8,40})(?:\n|$)/m
        ];

        for (const pattern of titlePatterns) {
          const match = content.match(pattern);
          if (match && match[1]) {
            let title = match[1].trim();
            title = title
              .replace(/[^\w\s-]/g, '')
              .replace(/\s+/g, ' ')
              .trim();

            if (title.length > 5 && title.length < 60) {
              fileName = title;
              break;
            }
          }
        }
      }
    }

    // If no title extracted, use job type-based naming
    if (!fileName) {
      const jobTypeNames = {
        'excel_formatting': 'Formatted_Data',
        'excel_analysis': 'Data_Analysis',
        'excel_dashboard': 'Performance_Dashboard',
        'excel_summary': 'Data_Summary',
        'document_general': 'Enhanced_Document',
        'document_business': 'Business_Document',
        'document_blog': 'Blog_Content',
        'document_academic': 'Academic_Paper',
        'document_development': 'Development_Copy'
      };

      fileName = jobTypeNames[result.jobType] || 'Enhanced_File';
    }

    // Add date suffix for uniqueness
    const now = new Date();
    const dateSuffix = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;

    // Ensure filename is not too long and clean
    fileName = fileName
      .replace(/\s+/g, '_')           // Replace spaces with underscores
      .replace(/[^\w\d_-]/g, '')      // Remove special characters
      .replace(/_+/g, '_')            // Replace multiple underscores with single
      .replace(/^_+|_+$/g, '')        // Remove leading/trailing underscores
      .substring(0, 40);              // Limit length

    // Add date suffix
    fileName = `${fileName}_${dateSuffix}`;

    // Final fallback if we still don't have a good name
    if (!fileName || fileName.length < 5) {
      fileName = `FileZen_Report_${dateSuffix}`;
    }

    // For Excel files, always process locally to ensure professional styling
    // For other formats, try server download first as fallback
    if (result.result?.enhanced_file_url && downloadFormat !== 'xlsx') {
      try {
        console.log(`Attempting to download ${downloadFormat} file from server storage`);
        // Try to download the enhanced file from Firebase Storage with proper CORS handling
        const response = await fetch(result.result.enhanced_file_url, {
          method: 'GET',
          mode: 'cors',
          credentials: 'same-origin',
          headers: {
            'Accept': 'application/octet-stream'
          }
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const blob = await response.blob();
        downloadBlob(blob, `${fileName}.${downloadFormat}`);
        return `Enhanced ${downloadFormat.toUpperCase()} file downloaded from storage!`;
      } catch (error) {
        console.error('Error downloading enhanced file from storage:', error);
        console.log('Falling back to local processing');
        // Fall back to generating a new file from the formatted data
      }
    } else if (downloadFormat === 'xlsx') {
      console.log('Excel format requested - processing locally for professional styling');
    }

    if (downloadFormat === 'xlsx') {
      console.log('Processing XLSX download with ExcelJS for professional styling');

      // Try dynamic import of ExcelJS
      let ExcelJSModule;
      try {
        ExcelJSModule = await import('exceljs');
        console.log('ExcelJS dynamically imported successfully');
      } catch (error) {
        console.error('Failed to import ExcelJS, falling back to basic XLSX library:', error);
        // Fall back to basic XLSX processing with some styling
        return await createBasicStyledExcel(enhancementText, formattedData, fileName, result, columnSpecifications || []);
      }

      // Create ExcelJS workbook
      const workbook = new ExcelJSModule.default.Workbook();

      let enhancedData = [];
      let columnSpecifications = [];
      let formattingChangesMade = [];
      let jsonData = null;

      // First try to extract and parse JSON data
      try {
        const jsonMatch = enhancementText.match(/(\{[\s\S]*\})/);
        if (jsonMatch) {
          const jsonStr = jsonMatch[1];
          console.log('Attempting to parse JSON string:', jsonStr.substring(0, 100) + '...');
          jsonData = JSON.parse(jsonStr);
          console.log('Successfully parsed JSON data:', Object.keys(jsonData));


          // Use title from JSON if available for professional file naming
          if (jsonData.title && typeof jsonData.title === 'string' && jsonData.title.length > 5) {
            const cleanTitle = jsonData.title
              .replace(/[^\w\s\d-]/g, '') // Remove special chars except spaces, hyphens
              .replace(/\s+/g, '_')      // Replace spaces with underscores
              .replace(/_+/g, '_');      // Replace multiple underscores with single

            if (cleanTitle.length >= 5) {
              fileName = cleanTitle;
              console.log('Using professional title from JSON for filename:', fileName);
            }
          }

          if (jsonData.formattedData && Array.isArray(jsonData.formattedData)) {
            enhancedData = jsonData.formattedData;
            columnSpecifications = jsonData.columnSpecifications || [];
            formattingChangesMade = jsonData.formattingChangesMade || [];
          } else {
            throw new Error('Invalid JSON structure: missing formattedData array');
          }
        } else {
          throw new Error('No JSON data found in the response');
        }
      } catch (error) {
        console.error('Error processing JSON data:', error);
        // Fall back to CSV parsing
        console.log('Falling back to CSV parsing for Excel');

        // Parse the formatted data as CSV or markdown table
        const rows = formattedData.split('\n')
          .map(line => line.trim())
          .filter(line => line.length > 0);

        // Check if we have a markdown table
        if (rows[0]?.includes('|')) {
          // Process markdown table
          rows.forEach((row, index) => {
            if (index === 1 && row.includes('|-')) {
              return; // Skip separator row
            }
            const cells = row.split('|')
              .filter(cell => cell.trim() !== '')
              .map(cell => cell.trim());
            enhancedData.push(cells);
          });
        } else {
          // Process regular CSV format
          rows.forEach(row => {
            // Handle quoted fields with commas
            const cells = [];
            let inQuote = false;
            let currentCell = '';

            for (let i = 0; i < row.length; i++) {
              const char = row[i];
              if (char === '"' && (i === 0 || row[i - 1] !== '\\')) {
                inQuote = !inQuote;
              } else if (char === ',' && !inQuote) {
                cells.push(currentCell.trim().replace(/^"(.*)"$/, '$1').replace(/""/g, '"'));
                currentCell = '';
              } else {
                currentCell += char;
              }
            }

            if (currentCell) {
              cells.push(currentCell.trim().replace(/^"(.*)"$/, '$1').replace(/""/g, '"'));
            }

            enhancedData.push(cells);
          });
        }
      }



      // Generate a meaningful worksheet name based on the content
      let worksheetName = 'Data Analysis';

      // Try to extract a meaningful name from the JSON data or content
      if (jsonData && jsonData.title) {
        // Clean the title for use as worksheet name (Excel has 31 character limit)
        worksheetName = jsonData.title
          .replace(/[^\w\s]/g, '') // Remove special characters
          .replace(/\s+/g, ' ')   // Replace multiple spaces with single
          .trim()
          .substring(0, 31);      // Excel worksheet name limit
      } else if (enhancedData.length > 0 && enhancedData[0].length > 0) {
        // Try to use the first header as worksheet name
        const firstHeader = enhancedData[0][0];
        if (firstHeader && typeof firstHeader === 'string' && firstHeader.length > 3) {
          worksheetName = firstHeader
            .replace(/[^\w\s]/g, '')
            .replace(/\s+/g, ' ')
            .trim()
            .substring(0, 31);
        }
      }

      // Ensure the name is not empty and is valid
      if (!worksheetName || worksheetName.length < 2) {
        worksheetName = 'Data Analysis';
      }



      // Create main data worksheet with professional styling
      const worksheet = workbook.addWorksheet(worksheetName);

      // Ensure we have some data to work with
      if (enhancedData.length === 0) {
        console.warn('No data found, creating basic Excel with message');
        enhancedData = [
          ['Message', 'Status'],
          ['No structured data found in the analysis result', 'Please check the original analysis']
        ];
      }

      if (enhancedData.length > 0) {
        // Set up columns with proper widths
        const headers = enhancedData[0];
        worksheet.columns = headers.map((header, idx) => {
          const spec = columnSpecifications.find(s => s.column === header);
          const dataType = spec?.dataType || 'text';

          let width = 18; // default
          switch (dataType) {
            case 'number': width = 15; break;
            case 'date': width = 16; break;
            case 'currency': width = 16; break;
            case 'percentage': width = 12; break;
            case 'status': width = 12; break;
            default: width = 22; break;
          }

          return {
            header: header,
            key: `col${idx}`,
            width: width
          };
        });

        // Add data rows
        for (let r = 1; r < enhancedData.length; r++) {
          const rowData = {};
          enhancedData[r].forEach((cellValue, c) => {
            rowData[`col${c}`] = cellValue;
          });
          worksheet.addRow(rowData);
        }

        // Style the header row (row 1)
        const headerRow = worksheet.getRow(1);
        headerRow.height = 25;
        headerRow.eachCell((cell, colNumber) => {
          cell.style = {
            font: { bold: true, size: 12, color: { argb: 'FFFFFFFF' } },
            fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF2D4A6B' } },
            alignment: { horizontal: 'center', vertical: 'middle' },
            border: {
              top: { style: 'medium', color: { argb: 'FF1A365D' } },
              left: { style: 'medium', color: { argb: 'FF1A365D' } },
              bottom: { style: 'medium', color: { argb: 'FF1A365D' } },
              right: { style: 'medium', color: { argb: 'FF1A365D' } }
            }
          };
        });

        // Style data rows with enhanced formatting
        for (let r = 2; r <= worksheet.rowCount; r++) {
          const row = worksheet.getRow(r);
          const isEvenRow = (r - 2) % 2 === 0;
          const defaultBgColor = isEvenRow ? 'FFF8FAFC' : 'FFFFFFFF';

          row.eachCell((cell, colNumber) => {
            const header = headers[colNumber - 1];
            const spec = columnSpecifications.find(s => s.column === header);
            const dataType = spec?.dataType || 'text';
            const cellValue = cell.value;

            // Base style
            cell.style = {
              font: { size: 11, color: { argb: 'FF374151' } },
              fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: defaultBgColor } },
              alignment: { horizontal: 'left', vertical: 'middle' },
              border: {
                top: { style: 'thin', color: { argb: 'FFD1D5DB' } },
                left: { style: 'thin', color: { argb: 'FFD1D5DB' } },
                bottom: { style: 'thin', color: { argb: 'FFD1D5DB' } },
                right: { style: 'thin', color: { argb: 'FFD1D5DB' } }
              }
            };

            // Apply data type specific formatting
            switch (dataType) {
              case 'number':
              case 'currency':
                cell.numFmt = dataType === 'currency' ? '$#,##0.00' : '#,##0.00';
                cell.alignment = { horizontal: 'center', vertical: 'middle' };

                // Parse numeric value if it's a string
                let numValue = cellValue;
                if (typeof cellValue === 'string') {
                  numValue = parseFloat(cellValue.replace(/[$,]/g, ''));
                }

                if (typeof numValue === 'number' && !isNaN(numValue)) {
                  cell.value = numValue;
                  // Color coding
                  if (numValue > 0) {
                    cell.font = { ...cell.font, color: { argb: 'FF059669' }, bold: Math.abs(numValue) > 1000 };
                  } else if (numValue < 0) {
                    cell.font = { ...cell.font, color: { argb: 'FFDC2626' }, bold: Math.abs(numValue) > 1000 };
                  }

                  // Special highlighting for very high/low values with theme colors
                  if (Math.abs(numValue) > 10000) {
                    cell.fill = {
                      type: 'pattern',
                      pattern: 'solid',
                      fgColor: { argb: numValue > 0 ? 'FFDCFCE7' : 'FFFEE2E2' }
                    };
                  }
                }
                break;

              case 'percentage':
              case 'growth':
              case 'change':
                cell.numFmt = dataType === 'percentage' ? '0.00%' : '+0.00%;-0.00%';
                cell.alignment = { horizontal: 'center', vertical: 'middle' };

                // Parse percentage value
                let pctValue = cellValue;
                if (typeof cellValue === 'string') {
                  pctValue = parseFloat(cellValue.replace(/[%+]/g, ''));
                }

                if (typeof pctValue === 'number' && !isNaN(pctValue)) {
                  cell.value = pctValue / 100; // Convert to decimal for percentage format
                  // Color coding
                  if (pctValue > 0) {
                    cell.font = { ...cell.font, color: { argb: 'FF059669' }, bold: Math.abs(pctValue) > 5 };
                  } else if (pctValue < 0) {
                    cell.font = { ...cell.font, color: { argb: 'FFDC2626' }, bold: Math.abs(pctValue) > 5 };
                  }

                  // Background highlighting for significant changes with theme colors
                  if (Math.abs(pctValue) > 10) {
                    cell.fill = {
                      type: 'pattern',
                      pattern: 'solid',
                      fgColor: { argb: pctValue > 0 ? 'FFD1FAE5' : 'FFFECACA' }
                    };
                  }
                }
                break;

              case 'status':
                cell.alignment = { horizontal: 'center', vertical: 'middle' };
                const statusValue = String(cellValue).toLowerCase();
                cell.font = { ...cell.font, bold: true, size: 10 };

                // Color-code status values
                if (statusValue.includes('good') || statusValue.includes('excellent') || statusValue.includes('green')) {
                  cell.font = { ...cell.font, color: { argb: 'FFFFFFFF' } };
                  cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF10B981' } };
                } else if (statusValue.includes('warning') || statusValue.includes('watch') || statusValue.includes('yellow')) {
                  cell.font = { ...cell.font, color: { argb: 'FF000000' } };
                  cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF59E0B' } };
                } else if (statusValue.includes('urgent') || statusValue.includes('critical') || statusValue.includes('red')) {
                  cell.font = { ...cell.font, color: { argb: 'FFFFFFFF' } };
                  cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFEF4444' } };
                }
                break;

              case 'date':
                cell.numFmt = 'yyyy-mm-dd';
                cell.font = { ...cell.font, color: { argb: 'FF6B7280' }, size: 10 };
                break;

              default: // text
                // Bold text for header-like content in first column
                if (colNumber === 1 && typeof cellValue === 'string' &&
                  (cellValue.includes('TOTAL') || cellValue.includes('SUMMARY') || cellValue.includes('KEY'))) {
                  cell.font = { ...cell.font, bold: true, color: { argb: 'FF1F2937' } };
                }

                // Auto-detect and format numeric values in text fields
                if (typeof cellValue === 'string') {
                  const numValue = parseFloat(cellValue.replace(/[$,%]/g, ''));
                  if (!isNaN(numValue) && cellValue.match(/[\d$%]/)) {
                    cell.alignment = { horizontal: 'center', vertical: 'middle' };

                    if (cellValue.includes('$')) {
                      cell.numFmt = '$#,##0.00';
                      cell.value = numValue;
                    } else if (cellValue.includes('%')) {
                      cell.numFmt = '0.00%';
                      cell.value = numValue / 100;
                    }

                    // Color coding for positive/negative values
                    if (numValue > 0) {
                      cell.font = { ...cell.font, color: { argb: 'FF059669' } };
                    } else if (numValue < 0) {
                      cell.font = { ...cell.font, color: { argb: 'FFDC2626' } };
                    }
                  }
                }
                break;
            }
          });
        }
      }



      // Generate and download the Excel file
      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });

      downloadBlob(blob, `${fileName}.xlsx`);
      return 'Professional Excel dashboard downloaded with full styling!';
    } else if (downloadFormat === 'docx') {
      console.log('Processing DOCX download');

      // Dynamically import the docx library
      const { Document, Packer, Paragraph, TextRun, HeadingLevel, AlignmentType, Table, TableRow, TableCell, WidthType, TableLayoutType, BorderStyle, VerticalAlign, LevelFormat, PageNumber, Footer, UnderlineType } = await import('docx');

      // Clean up the formatted data by removing common prefixes that AI might add
      const cleanupPrefixes = [
        "Below is the complete improved version of your document:",
        "ENHANCED DOCUMENTENHANCED DOCUMENT",
        "Here is the enhanced version of your document:",
        "Enhanced Document:",
        "Please find below the enhanced document:"
      ];

      // Remove any of these prefixes if they appear at the beginning of the text
      let cleanedFormattedData = formattedData;
      for (const prefix of cleanupPrefixes) {
        if (cleanedFormattedData.includes(prefix)) {
          cleanedFormattedData = cleanedFormattedData.substring(cleanedFormattedData.indexOf(prefix) + prefix.length).trim();
        }
      }

      // Try to extract a title from the content for better filename
      let documentTitle = "";
      let documentSubtitle = "";
      const titleMatch = cleanedFormattedData.match(/^#\s+(.+?)(?:\n|$)/);
      if (titleMatch) {
        documentTitle = titleMatch[1].trim();
        // Update filename with extracted title
        if (documentTitle.length > 5 && documentTitle.length < 100) {
          const cleanTitleForFilename = documentTitle
            .replace(/[^\w\s\d-]/g, '') // Remove special chars except spaces, hyphens
            .replace(/\s+/g, '_')      // Replace spaces with underscores
            .replace(/_+/g, '_')       // Replace multiple underscores with single
            .substring(0, 50);         // Limit length

          if (cleanTitleForFilename.length >= 5) {
            fileName = cleanTitleForFilename;
            console.log('Using extracted title for filename:', fileName);
          }
        }

        // Remove the title from the formatted data to avoid duplication
        let remainingData = cleanedFormattedData.replace(/^#\s+(.+?)(?:\n|$)/, "").trim();

        // Check for subtitle (italic text right after title)
        const subtitleMatch = remainingData.match(/^\*(.+?)\*(?:\n|$)/);
        if (subtitleMatch) {
          documentSubtitle = subtitleMatch[1].trim();
          // Remove the subtitle from the formatted data
          cleanedFormattedData = remainingData.replace(/^\*(.+?)\*(?:\n|$)/, "").trim();
        } else {
          cleanedFormattedData = remainingData;
        }
      } else {
        // Try to find a title in the first line if it's not a markdown heading
        const firstLine = cleanedFormattedData.split('\n')[0].trim();
        if (firstLine && firstLine.length < 100 && firstLine.length > 5) { // Reasonable title length
          documentTitle = firstLine;

          // Update filename with first line title
          const cleanTitleForFilename = firstLine
            .replace(/[^\w\s\d-]/g, '') // Remove special chars except spaces, hyphens
            .replace(/\s+/g, '_')      // Replace spaces with underscores
            .replace(/_+/g, '_')       // Replace multiple underscores with single
            .substring(0, 50);         // Limit length

          if (cleanTitleForFilename.length >= 5) {
            fileName = cleanTitleForFilename;
            console.log('Using first line as title for filename:', fileName);
          }

          // Check if second line could be a subtitle
          const lines = cleanedFormattedData.split('\n');
          if (lines.length > 1) {
            const secondLine = lines[1].trim();
            // Check if it's italic or looks like a subtitle
            if ((secondLine.startsWith('*') && secondLine.endsWith('*')) ||
              (secondLine.length < 150 && secondLine.length > 10 && !secondLine.includes('.'))) {
              documentSubtitle = secondLine.replace(/^\*/, '').replace(/\*$/, '').trim();
              // Remove both title and subtitle
              cleanedFormattedData = lines.slice(2).join('\n').trim();
            } else {
              // Remove just the title
              cleanedFormattedData = lines.slice(1).join('\n').trim();
            }
          } else {
            cleanedFormattedData = "";
          }
        }
      }

      // Build up the Word document structure
      const paragraphs = [];

      // Add the title if we found one
      if (documentTitle) {
        // Add main title
        paragraphs.push(
          new Paragraph({
            alignment: AlignmentType.CENTER,
            spacing: { before: 0, after: documentSubtitle ? 200 : 400 },
            children: [
              new TextRun({
                text: documentTitle,
                bold: true,
                size: 36, // 18pt font size for main title
                color: "1A365D", // Professional dark blue
                font: "Calibri",
              })
            ],
            border: {
              bottom: {
                color: "2B6CB0",
                size: 10,
                style: BorderStyle.SINGLE,
              },
            },
          })
        );

        // Add subtitle if we have one
        if (documentSubtitle) {
          paragraphs.push(
            new Paragraph({
              alignment: AlignmentType.CENTER,
              spacing: { before: 0, after: 400 },
              children: [
                new TextRun({
                  text: documentSubtitle,
                  italics: true,
                  size: 24, // 12pt for subtitle
                  color: "4A5568", // Professional gray
                  font: "Calibri",
                })
              ],
            })
          );
        }

        // Add separator line after title section
        paragraphs.push(
          new Paragraph({
            text: "",
            spacing: { after: 300 },
            border: {
              bottom: {
                color: "E2E8F0",
                size: 4,
                style: BorderStyle.SINGLE,
              },
            },
          })
        );
      }

      // Enhanced text parsing with better formatting
      const lines = cleanedFormattedData.split('\n');

      const parseInlineFormatting = (text) => {
        const runs = [];
        let currentIndex = 0;

        // Enhanced regex to catch various formatting patterns
        const patterns = [
          { regex: /\*\*\*(.*?)\*\*\*/g, bold: true, italics: true },  // ***bold and italic***
          { regex: /\*\*(.*?)\*\*/g, bold: true },                    // **bold**
          { regex: /\*(.*?)\*/g, italics: true },                     // *italic*
        ];

        const matches = [];

        // Find all matches
        patterns.forEach(pattern => {
          let match;
          while ((match = pattern.regex.exec(text)) !== null) {
            matches.push({
              start: match.index,
              end: match.index + match[0].length,
              content: match[1],
              bold: pattern.bold || false,
              italics: pattern.italics || false,
              original: match[0]
            });
          }
        });

        // Sort matches by start position
        matches.sort((a, b) => a.start - b.start);

        // Remove overlapping matches (keep the first one)
        const cleanMatches = [];
        let lastEnd = 0;
        matches.forEach(match => {
          if (match.start >= lastEnd) {
            cleanMatches.push(match);
            lastEnd = match.end;
          }
        });

        // Build text runs
        cleanMatches.forEach(match => {
          // Add text before the match
          if (match.start > currentIndex) {
            const beforeText = text.substring(currentIndex, match.start);
            if (beforeText.trim()) {
              runs.push(new TextRun({
                text: beforeText,
                size: 22, // 11pt
                font: "Calibri",
                color: "2D3748"
              }));
            }
          }

          // Add the formatted text
          runs.push(new TextRun({
            text: match.content,
            bold: match.bold,
            italics: match.italics,
            size: match.bold ? 24 : 22, // Make bold text slightly larger
            font: "Calibri",
            color: match.bold ? "1A365D" : "2D3748" // Make bold text darker blue
          }));

          currentIndex = match.end;
        });

        // Add remaining text
        if (currentIndex < text.length) {
          const remainingText = text.substring(currentIndex);
          if (remainingText.trim()) {
            runs.push(new TextRun({
              text: remainingText,
              size: 22, // 11pt
              font: "Calibri",
              color: "2D3748"
            }));
          }
        }

        // If no formatting was found, return a single run
        if (runs.length === 0) {
          runs.push(new TextRun({
            text: text,
            size: 22, // 11pt
            font: "Calibri",
            color: "2D3748"
          }));
        }

        return runs;
      };

      // Process each line for enhanced formatting
      for (let i = 0; i < lines.length; i++) {
        const line = lines[i].trim();

        // Skip empty lines (but add spacing)
        if (!line) {
          paragraphs.push(new Paragraph({ text: "", spacing: { after: 100 } }));
          continue;
        }

        // Handle different markdown elements
        if (line.startsWith('# ')) {
          // Main heading (H1) - should be centered and prominent
          const headingText = line.substring(2).trim();
          paragraphs.push(
            new Paragraph({
              alignment: AlignmentType.CENTER,
              spacing: { before: 400, after: 300 },
              children: [
                new TextRun({
                  text: headingText,
                  bold: true,
                  size: 32, // 16pt font size
                  color: "1A365D", // Professional dark blue
                  font: "Calibri",
                })
              ],
              border: {
                bottom: {
                  color: "2B6CB0",
                  size: 8,
                  style: BorderStyle.SINGLE,
                },
              },
            })
          );
        } else if (line.startsWith('## ')) {
          // Section heading (H2)
          const headingText = line.substring(3).trim();
          paragraphs.push(
            new Paragraph({
              spacing: { before: 300, after: 200 },
              children: [
                new TextRun({
                  text: headingText,
                  bold: true,
                  size: 28, // 14pt font size
                  color: "2D3748", // Professional dark gray
                  font: "Calibri",
                })
              ],
              border: {
                bottom: {
                  color: "4299E1",
                  size: 4,
                  style: BorderStyle.SINGLE,
                },
              },
            })
          );
        } else if (line.startsWith('### ')) {
          // Subsection heading (H3)
          const headingText = line.substring(4).trim();
          paragraphs.push(
            new Paragraph({
              spacing: { before: 250, after: 150 },
              children: [
                new TextRun({
                  text: headingText,
                  bold: true,
                  size: 26, // 13pt font size
                  color: "4A5568", // Medium gray
                  font: "Calibri",
                })
              ],
            })
          );
        } else if (line.match(/^[🎯📈💡⚡🚀📊🔑✨💼🌟📝🎪🔥⭐🏆💪🎨📱💰🔍📋💎🌈🎊📌]\s*\d+\.\s*/)) {
          // Numbered list with emoji (e.g., "🎯 1. The Brain Dump Method")
          const match = line.match(/^([🎯📈💡⚡🚀📊🔑✨💼🌟📝🎪🔥⭐🏆💪🎨📱💰🔍📋💎🌈🎊📌])\s*(\d+)\.\s*(.+)$/);
          if (match) {
            const emoji = match[1];
            const number = match[2];
            const content = match[3];

            // Parse the content for bold formatting
            const formattedContent = parseInlineFormatting(content);

            paragraphs.push(
              new Paragraph({
                spacing: { before: 150, after: 100 },
                indent: { left: 360 }, // Indent for list items
                children: [
                  new TextRun({
                    text: `${emoji} ${number}. `,
                    bold: true,
                    size: 22,
                    color: "2B6CB0", // Blue for emoji and number
                    font: "Calibri",
                  }),
                  ...formattedContent
                ],
              })
            );
          }
        } else if (line.match(/^\d+\.\s/)) {
          // Regular numbered list
          const match = line.match(/^(\d+)\.\s*(.+)$/);
          if (match) {
            const number = match[1];
            const content = match[2];
            const formattedContent = parseInlineFormatting(content);

            paragraphs.push(
              new Paragraph({
                spacing: { before: 100, after: 100 },
                indent: { left: 360, hanging: 260 },
                children: [
                  new TextRun({
                    text: `${number}. `,
                    bold: true,
                    size: 22,
                    color: "2B6CB0", // Blue for number
                    font: "Calibri",
                  }),
                  ...formattedContent
                ],
              })
            );
          }
        } else if (line.startsWith('- ') || line.startsWith('* ')) {
          // Bullet list
          const content = line.substring(2).trim();
          const formattedContent = parseInlineFormatting(content);

          paragraphs.push(
            new Paragraph({
              spacing: { before: 100, after: 100 },
              indent: { left: 360, hanging: 260 },
              children: [
                new TextRun({
                  text: "• ",
                  bold: true,
                  color: "3182CE", // Blue bullet
                  size: 22,
                  font: "Calibri",
                }),
                ...formattedContent
              ],
            })
          );
        } else if (line.startsWith('> ')) {
          // Blockquote
          const content = line.substring(2).trim();
          const formattedContent = parseInlineFormatting(content);

          paragraphs.push(
            new Paragraph({
              spacing: { before: 200, after: 200 },
              indent: { left: 720 },
              children: formattedContent,
              border: {
                left: {
                  color: "3182CE",
                  size: 12,
                  style: BorderStyle.SINGLE,
                },
              },
              shading: {
                fill: "F7FAFC",
              },
            })
          );
        } else if (line.includes('|') && i > 0 && lines[i - 1].includes('|')) {
          // Table handling - skip for now, will be handled separately
          continue;
        } else {
          // Regular paragraph
          const formattedContent = parseInlineFormatting(line);

          paragraphs.push(
            new Paragraph({
              spacing: { before: 100, after: 100 },
              children: formattedContent,
            })
          );
        }
      }

      // Create a footer with page numbers
      const footer = new Footer({
        children: [
          new Paragraph({
            alignment: AlignmentType.CENTER,
            children: [
              new TextRun({
                children: ["Page ", PageNumber.CURRENT, " of ", PageNumber.TOTAL_PAGES],
                size: 18, // 9pt
                color: "7F8C8D",
                font: "Calibri",
              }),
            ],
          }),
        ],
      });

      // Create the document with proper styling
      const doc = new Document({
        styles: {
          default: {
            document: {
              run: {
                font: "Calibri",
                size: 22, // 11pt default
              },
            },
          },
        },
        sections: [
          {
            properties: {
              page: {
                margin: {
                  top: 1440, // 1 inch
                  right: 1440,
                  bottom: 1440,
                  left: 1440,
                },
              },
            },
            children: paragraphs,
            footers: {
              default: footer,
            },
          },
        ],
      });

      const blob = await Packer.toBlob(doc);
      downloadBlob(blob, `${fileName}.docx`);
      return 'Enhanced Word document downloaded!';

    } else if (downloadFormat === 'csv') {
      console.log('Processing CSV download');

      // Split the formatted data into rows
      const rows = formattedData.split('\n')
        .map(line => line.trim())
        .filter(line => line.length > 0);

      // Process the rows into proper CSV format
      const csvRows = [];

      // Add header
      csvRows.push('"ENHANCED CSV DATA - FileZen AI"');
      csvRows.push('"Generated: ' + new Date().toLocaleDateString() + '"');
      csvRows.push('"Job Type: ' + (result.jobType || 'DATA ANALYSIS').replace('_', ' ').toUpperCase() + '"');
      csvRows.push('""');

      // Process each row
      rows.forEach(row => {
        if (row.includes('|')) {
          // Convert markdown table row to CSV
          const cells = row.split('|')
            .filter(cell => cell.trim() !== '')
            .map(cell => cell.trim());

          if (!row.includes('|-')) { // Skip separator rows
            csvRows.push('"' + cells.join('","') + '"');
          }
        } else {
          // Handle regular CSV format
          if (row.includes('"')) {
            csvRows.push(row); // Already properly formatted
          } else {
            // Add quotes around each field
            csvRows.push('"' + row.split(',')
              .map(cell => cell.trim().replace(/"/g, '""'))
              .join('","') + '"');
          }
        }
      });

      const csvContent = csvRows.join('\n');
      const blob = new Blob([csvContent], { type: 'text/csv' });
      downloadBlob(blob, `${fileName}.csv`);
      return 'Enhanced CSV file downloaded!';

    } else if (downloadFormat === 'pdf') {
      console.log('Processing PDF download with emoji support');

      // Enhanced emoji handling function
      const processEmojiText = (text) => {
        // Convert common emojis to text representations for better PDF compatibility
        const emojiMap = {
          '🎯': '[TARGET]',
          '📈': '[CHART]',
          '💡': '[IDEA]',
          '⚡': '[LIGHTNING]',
          '🚀': '[ROCKET]',
          '📊': '[BAR_CHART]',
          '🔑': '[KEY]',
          '✨': '[SPARKLES]',
          '💼': '[BRIEFCASE]',
          '🌟': '[STAR]',
          '📝': '[MEMO]',
          '🎪': '[CIRCUS]',
          '🔥': '[FIRE]',
          '⭐': '[STAR2]',
          '🏆': '[TROPHY]',
          '💪': '[MUSCLE]',
          '🎨': '[ART]',
          '📱': '[PHONE]',
          '💰': '[MONEY]',
          '🔍': '[SEARCH]',
          '📋': '[CLIPBOARD]',
          '💎': '[GEM]',
          '🌈': '[RAINBOW]',
          '🎊': '[CONFETTI]',
          '📌': '[PIN]'
        };

        let processedText = text;

        // Replace emojis with text representations
        Object.entries(emojiMap).forEach(([emoji, replacement]) => {
          processedText = processedText.replace(new RegExp(emoji, 'g'), replacement);
        });

        // Also handle any remaining unicode emojis with a generic pattern
        processedText = processedText.replace(/[\u{1F600}-\u{1F64F}]|[\u{1F300}-\u{1F5FF}]|[\u{1F680}-\u{1F6FF}]|[\u{1F1E0}-\u{1F1FF}]|[\u{2600}-\u{26FF}]|[\u{2700}-\u{27BF}]/gu, '');

        return processedText;
      };

      const html = marked(formattedData);
      const container = document.createElement('div');
      container.innerHTML = html;

      const doc = new jsPDF({ orientation: 'p', unit: 'pt', format: 'a4' });
      doc.setFont('Helvetica', 'normal');

      const margin = 40;
      const maxLineWidth = doc.internal.pageSize.getWidth() - margin * 2;
      let y = margin;

      // Enhanced addText function with emoji support and better text wrapping
      const addText = (text, options, isListItem = false) => {
        if (!text || !text.trim()) return;
        if (y > doc.internal.pageSize.getHeight() - margin - 40) {
          doc.addPage();
          y = margin;
          doc.setFont('Helvetica', 'normal');
        }

        doc.setFontSize(options.fontSize || 10);
        doc.setFont('Helvetica', options.fontStyle || 'normal');

        const indent = isListItem ? (options.indent || 0) + 20 : (options.indent || 0);
        const finalMaxLineWidth = maxLineWidth - indent;

        // Process text to handle emojis
        const textToRender = processEmojiText(text.replace(/–/g, '-').trim());

        // Handle emoji-style numbered lists with better formatting
        let processedText = textToRender;

        // Enhanced formatting for lists with emoji indicators
        if (textToRender.match(/^\[[\w_]+\]\s*\d+\.\s*/)) {
          const match = textToRender.match(/^(\[[\w_]+\])\s*(\d+)\.\s*(.+)$/);
          if (match) {
            const emojiIndicator = match[1];
            const number = match[2];
            const content = match[3];

            // Set color for the indicator
            doc.setTextColor(37, 99, 235); // Professional blue
            doc.setFont('Helvetica', 'bold');

            // Calculate prefix width more accurately
            const prefix = `${emojiIndicator} ${number}. `;
            const prefixWidth = doc.getTextWidth(prefix);

            // Render prefix
            doc.text(prefix, margin + indent, y);

            // Reset styling for content
            doc.setTextColor(31, 41, 55); // Dark gray
            doc.setFont('Helvetica', options.fontStyle || 'normal');

            // Split content text to fit remaining space
            const contentMaxWidth = finalMaxLineWidth - prefixWidth - 5; // Extra margin
            const contentLines = doc.splitTextToSize(content, contentMaxWidth);

            // Render content with proper wrapping
            let contentY = y;
            contentLines.forEach((line, index) => {
              if (contentY > doc.internal.pageSize.getHeight() - margin - 20) {
                doc.addPage();
                contentY = margin;
                // Reset styling after page break
                doc.setTextColor(31, 41, 55);
                doc.setFont('Helvetica', options.fontStyle || 'normal');
                doc.setFontSize(options.fontSize || 10);
              }

              const xPos = index === 0 ? margin + indent + prefixWidth : margin + indent + prefixWidth;
              doc.text(line, xPos, contentY);

              // Always increment Y for proper line spacing
              contentY += doc.getLineHeight() * 1.4;
            });

            y = contentY + (options.spacing || 8);
            return;
          }
        }

        // Regular text rendering with proper wrapping
        const defaultColor = options.color || [31, 41, 55]; // Default dark gray
        if (Array.isArray(defaultColor)) {
          doc.setTextColor(defaultColor[0], defaultColor[1], defaultColor[2]);
        } else {
          doc.setTextColor(defaultColor);
        }

        // Split text to fit within bounds
        const splitText = doc.splitTextToSize(processedText, finalMaxLineWidth);

        // Render each line, handling page breaks
        splitText.forEach((line, index) => {
          if (y > doc.internal.pageSize.getHeight() - margin - 20) {
            doc.addPage();
            y = margin;
            doc.setFont('Helvetica', options.fontStyle || 'normal');
            doc.setFontSize(options.fontSize || 10);
            const resetColor = options.color || [31, 41, 55];
            if (Array.isArray(resetColor)) {
              doc.setTextColor(resetColor[0], resetColor[1], resetColor[2]);
            } else {
              doc.setTextColor(resetColor);
            }
          }

          doc.text(line, margin + indent, y);

          // More reasonable line spacing
          y += doc.getLineHeight() * 1.2; // Further reduced for tighter text
        });

        // Reduced final spacing after the text block
        y += (options.spacing || 4); // Further reduced default spacing
      };

      // Enhanced element processing with better emoji handling and styling
      const elements = Array.from(container.childNodes);

      for (const element of elements) {
        const tagName = element.tagName?.toLowerCase();
        switch (tagName) {
          case 'h1':
            // Check if this is the first element (no extra spacing before)
            const isFirstH1 = Array.from(elements).indexOf(element) === 0;
            if (!isFirstH1) {
              y += 20; // Add space before heading (except first one)
            }

            // Main headings without underline
            addText(element.textContent, {
              fontSize: 24,
              fontStyle: 'bold',
              spacing: 8, // Reduced from 15
              color: [30, 58, 138] // Deep blue
            });
            break;
          case 'h2':
            // Check if previous element exists and add space before heading
            const prevElementH2 = elements[Array.from(elements).indexOf(element) - 1];
            if (prevElementH2) {
              y += 16; // Add space before heading
            }

            addText(element.textContent, {
              fontSize: 18,
              fontStyle: 'bold',
              spacing: 6, // Reduced from 12
              color: [51, 65, 85] // Professional dark gray
            });
            break;
          case 'h3':
            // Check if previous element exists and add space before heading
            const prevElementH3 = elements[Array.from(elements).indexOf(element) - 1];
            if (prevElementH3) {
              y += 12; // Add space before heading
            }

            addText(element.textContent, {
              fontSize: 16,
              fontStyle: 'bold',
              spacing: 4, // Reduced from 10
              color: [71, 85, 105] // Medium gray
            });
            break;
          case 'p':
            const hasOnlyStrongChild = element.children.length === 1 && (element.children[0].tagName === 'STRONG' || element.children[0].tagName === 'B');
            const isAllBold = hasOnlyStrongChild && element.textContent.trim() === element.children[0].textContent.trim();

            if (isAllBold) {
              addText(element.textContent, {
                fontSize: 12,
                fontStyle: 'bold',
                spacing: 4, // Reduced from 8
                color: [17, 24, 39] // Very dark gray
              });
            } else {
              addText(element.textContent, {
                fontSize: 11,
                spacing: 3, // Reduced from 6
                color: [55, 65, 81] // Regular text gray
              });
            }
            break;
          case 'blockquote':
            // Simple blockquote styling without complex background
            const quoteText = element.textContent;

            // Check for page break
            if (y > doc.internal.pageSize.getHeight() - margin - 50) {
              doc.addPage();
              y = margin;
            }

            // Add some spacing before blockquote
            const prevElementQuote = elements[Array.from(elements).indexOf(element) - 1];
            if (prevElementQuote) {
              y += 10; // Consistent spacing before blockquote
            }

            // Draw simple left border
            doc.setDrawColor(59, 130, 246);
            doc.setLineWidth(3);

            // Calculate quote height more accurately
            doc.setFont('Helvetica', 'italic');
            doc.setFontSize(11);
            const quoteLines = doc.splitTextToSize(quoteText, maxLineWidth - 30);
            const quoteHeight = quoteLines.length * doc.getLineHeight() * 1.4;

            // Draw left border
            doc.line(margin + 15, y - 5, margin + 15, y + quoteHeight + 5);

            // Set text color and style
            doc.setTextColor(75, 85, 99);
            doc.setFont('Helvetica', 'italic');
            doc.setFontSize(11);

            // Render quote text line by line
            quoteLines.forEach((line, index) => {
              if (y > doc.internal.pageSize.getHeight() - margin - 20) {
                doc.addPage();
                y = margin;
                // Reset styling after page break
                doc.setTextColor(75, 85, 99);
                doc.setFont('Helvetica', 'italic');
                doc.setFontSize(11);
              }

              doc.text(line, margin + 25, y);
              y += doc.getLineHeight() * 1.4;
            });

            // Add spacing after blockquote
            y += 6;
            break;
          case 'ul':
          case 'ol':
            // Add spacing before lists
            const prevElementList = elements[Array.from(elements).indexOf(element) - 1];
            if (prevElementList) {
              y += 8; // Consistent spacing before lists
            }

            Array.from(element.getElementsByTagName('li')).forEach((li, i) => {
              // Handle lists with better emoji support
              const listText = li.textContent;

              // Check if this is an emoji-numbered list
              if (listText.match(/^[🎯📈💡⚡🚀📊🔑✨💼🌟📝🎪🔥⭐🏆💪🎨📱💰🔍📋💎🌈🎊📌]/)) {
                // Let the main text processor handle emoji lists
                addText(listText, {
                  fontSize: 11,
                  spacing: 3, // Reduced spacing between list items
                  color: [55, 65, 81]
                }, true);
              } else {
                // Regular lists with enhanced styling
                const prefix = tagName === 'ul' ? '• ' : `${i + 1}. `;

                // Check for page break before rendering list item
                if (y > doc.internal.pageSize.getHeight() - margin - 30) {
                  doc.addPage();
                  y = margin;
                }

                // Set bullet/number color and style
                doc.setTextColor(37, 99, 235); // Blue for bullets/numbers
                doc.setFont('Helvetica', 'bold');
                doc.setFontSize(11); // Consistent size
                doc.text(prefix, margin + 20, y);

                // Calculate prefix width
                const prefixWidth = doc.getTextWidth(prefix);

                // Reset for list content with consistent styling
                doc.setTextColor(55, 65, 81);
                doc.setFont('Helvetica', 'normal');
                doc.setFontSize(11); // Consistent size

                // Split and render list text
                const listLines = doc.splitTextToSize(listText, maxLineWidth - 20 - prefixWidth - 10);

                // Render first line at current Y position
                doc.text(listLines[0], margin + 20 + prefixWidth, y);

                // Render additional lines if any
                if (listLines.length > 1) {
                  for (let lineIndex = 1; lineIndex < listLines.length; lineIndex++) {
                    y += doc.getLineHeight() * 1.2; // Reduced from 1.3
                    if (y > doc.internal.pageSize.getHeight() - margin - 20) {
                      doc.addPage();
                      y = margin;
                    }
                    doc.text(listLines[lineIndex], margin + 20 + prefixWidth, y);
                  }
                }

                // Move to next line after list item
                y += doc.getLineHeight() * 1.1 + 1; // Reduced spacing between list items
              }
            });
            y += 4; // Spacing after list
            break;
          case 'table':
            // Enhanced table styling
            if (y > doc.internal.pageSize.getHeight() - margin - 80) {
              doc.addPage();
              y = margin;
              doc.setFont('Helvetica', 'normal');
            }

            const head = Array.from(element.querySelectorAll('thead th')).map(th => ({
              content: processEmojiText(th.textContent),
              styles: { fillColor: [37, 99, 235], textColor: [255, 255, 255], fontStyle: 'bold' }
            }));

            const body = Array.from(element.querySelectorAll('tbody tr')).map(tr =>
              Array.from(tr.querySelectorAll('td')).map(td => processEmojiText(td.textContent))
            );

            autoTable(doc, {
              head: [head],
              body: body,
              startY: y,
              margin: { left: margin, right: margin },
              styles: {
                fontSize: 10,
                cellPadding: 8,
                lineColor: [226, 232, 240],
                lineWidth: 0.5,
                font: 'Helvetica',
                textColor: [55, 65, 81]
              },
              headStyles: {
                fillColor: [37, 99, 235],
                textColor: [255, 255, 255],
                fontStyle: 'bold',
                fontSize: 11
              },
              alternateRowStyles: {
                fillColor: [248, 250, 252]
              },
              tableLineColor: [37, 99, 235],
              tableLineWidth: 0.75,
            });
            y = doc.lastAutoTable.finalY + 20;
            break;
          case 'pre':
            // Enhanced code block styling
            if (y > doc.internal.pageSize.getHeight() - margin - 60) {
              doc.addPage();
              y = margin;
              doc.setFont('Helvetica', 'normal');
            }

            const codeText = processEmojiText(element.textContent);
            const splitCode = doc.splitTextToSize(codeText, maxLineWidth - 20);
            const rectHeight = (splitCode.length * 10 * 1.2) + 16;

            // Draw code background with border
            doc.setFillColor(248, 250, 252);
            doc.setDrawColor(203, 213, 225);
            doc.setLineWidth(1);
            doc.rect(margin, y - 8, maxLineWidth, rectHeight, 'FD');

            // Set code font and style
            doc.setFont('Courier', 'normal');
            doc.setFontSize(9);
            doc.setTextColor(51, 65, 85);

            // Render code text
            let codeY = y + 5;
            splitCode.forEach(line => {
              doc.text(line, margin + 10, codeY);
              codeY += 10 * 1.2;
            });

            y += rectHeight + 15;

            // Reset font
            doc.setFont('Helvetica', 'normal');
            doc.setTextColor(55, 65, 81);
            break;
          default:
            // Handle text nodes that aren't in specific elements
            if (element.nodeType === Node.TEXT_NODE && element.textContent?.trim()) {
              addText(element.textContent, {
                fontSize: 11,
                spacing: 10,
                color: [55, 65, 81]
              });
            }
            break;
        }
      }

      doc.save(`${fileName}.pdf`);
      return 'Enhanced PDF file with professional styling downloaded!';

    } else {
      // For other formats (docx, txt), use the formatted data as is
      const content = formattedData;
      const blob = new Blob([content], { type: 'text/plain;charset=utf-8' });
      downloadBlob(blob, `${fileName}.${downloadFormat}`);
      return `Enhanced ${downloadFormat.toUpperCase()} file downloaded!`;
    }

  } catch (error) {
    console.error('Error in downloadEnhancedFile:', error);
    return `Error: ${error.message}`;
  }
};

// Fallback function using basic XLSX library with enhanced formatting
const createBasicStyledExcel = async (enhancementText, formattedData, fileName, result, columnSpecifications) => {
  console.log('Creating enhanced Excel file with basic XLSX library');

  const workbook = XLSX.utils.book_new();
  let enhancedData = [];
  let jsonData = null;

  // Parse data (same logic as before)
  try {
    const jsonMatch = enhancementText.match(/(\{[\s\S]*\})/);
    if (jsonMatch) {
      const jsonStr = jsonMatch[1];
      jsonData = JSON.parse(jsonStr);
      if (jsonData.formattedData && Array.isArray(jsonData.formattedData)) {
        enhancedData = jsonData.formattedData;
      } else {
        throw new Error('Invalid JSON structure');
      }
    } else {
      throw new Error('No JSON data found');
    }
  } catch (error) {
    // Fall back to CSV parsing
    const rows = formattedData.split('\n')
      .map(line => line.trim())
      .filter(line => line.length > 0);

    if (rows[0]?.includes('|')) {
      rows.forEach((row, index) => {
        if (index === 1 && row.includes('|-')) return;
        const cells = row.split('|')
          .filter(cell => cell.trim() !== '')
          .map(cell => cell.trim());
        enhancedData.push(cells);
      });
    } else {
      rows.forEach(row => {
        const cells = [];
        let inQuote = false;
        let currentCell = '';

        for (let i = 0; i < row.length; i++) {
          const char = row[i];
          if (char === '"' && (i === 0 || row[i - 1] !== '\\')) {
            inQuote = !inQuote;
          } else if (char === ',' && !inQuote) {
            cells.push(currentCell.trim().replace(/^"(.*)"$/, '$1').replace(/""/g, '"'));
            currentCell = '';
          } else {
            currentCell += char;
          }
        }

        if (currentCell) {
          cells.push(currentCell.trim().replace(/^"(.*)"$/, '$1').replace(/""/g, '"'));
        }

        enhancedData.push(cells);
      });
    }
  }

  // Ensure we have some data to work with
  if (enhancedData.length === 0) {
    console.warn('No data found in fallback, creating basic Excel with message');
    enhancedData = [
      ['Message', 'Status'],
      ['No structured data found in the analysis result', 'Please check the original analysis']
    ];
  }

  if (enhancedData.length > 0) {
    // Create worksheet with basic styling support
    let worksheet = XLSX.utils.aoa_to_sheet(enhancedData);

    // Apply basic column widths
    worksheet['!cols'] = enhancedData[0].map((header, idx) => {
      const spec = columnSpecifications.find(s => s.column === header);
      const dataType = spec?.dataType || 'text';

      switch (dataType) {
        case 'number': return { wch: 15 };
        case 'date': return { wch: 16 };
        case 'currency': return { wch: 16 };
        case 'percentage': return { wch: 12 };
        case 'status': return { wch: 12 };
        default: return { wch: 22 };
      }
    });

    // Apply basic cell formatting
    for (let r = 0; r < enhancedData.length; r++) {
      for (let c = 0; c < enhancedData[r].length; c++) {
        const cellRef = XLSX.utils.encode_cell({ r, c });
        if (!worksheet[cellRef]) continue;

        const cellValue = enhancedData[r][c];

        if (!worksheet[cellRef].s) worksheet[cellRef].s = {};

        if (r === 0) {
          // Header row
          worksheet[cellRef].s = {
            font: { bold: true, color: { rgb: 'FFFFFF' }, size: 12 },
            fill: { fgColor: { rgb: '2D4A6B' }, type: 'solid' },
            alignment: { horizontal: 'center', vertical: 'center' },
            border: {
              top: { style: 'medium', color: { rgb: '1A365D' } },
              left: { style: 'medium', color: { rgb: '1A365D' } },
              bottom: { style: 'medium', color: { rgb: '1A365D' } },
              right: { style: 'medium', color: { rgb: '1A365D' } }
            }
          };
        } else {
          // Data rows
          const isEvenRow = (r - 1) % 2 === 0;
          const header = enhancedData[0][c];
          const spec = columnSpecifications.find(s => s.column === header);
          const dataType = spec?.dataType || 'text';

          worksheet[cellRef].s = {
            font: { size: 11, color: { rgb: '374151' } },
            fill: { fgColor: { rgb: isEvenRow ? 'F8FAFC' : 'FFFFFF' }, type: 'solid' },
            border: {
              top: { style: 'thin', color: { rgb: 'D1D5DB' } },
              left: { style: 'thin', color: { rgb: 'D1D5DB' } },
              bottom: { style: 'thin', color: { rgb: 'D1D5DB' } },
              right: { style: 'thin', color: { rgb: 'D1D5DB' } }
            },
            alignment: { horizontal: 'left', vertical: 'center' }
          };

          // Apply data type specific formatting
          switch (dataType) {
            case 'number':
            case 'currency':
              worksheet[cellRef].z = dataType === 'currency' ? '$#,##0.00' : '#,##0.00';
              worksheet[cellRef].s.alignment.horizontal = 'center';

              const numValue = parseFloat(String(cellValue).replace(/[$,]/g, ''));
              if (!isNaN(numValue)) {
                if (numValue > 0) {
                  worksheet[cellRef].s.font.color = { rgb: '059669' };
                } else if (numValue < 0) {
                  worksheet[cellRef].s.font.color = { rgb: 'DC2626' };
                }
                if (Math.abs(numValue) > 1000) {
                  worksheet[cellRef].s.font.bold = true;
                }
              }
              break;

            case 'percentage':
              worksheet[cellRef].z = '0.00%';
              worksheet[cellRef].s.alignment.horizontal = 'center';

              const pctValue = parseFloat(String(cellValue).replace(/[%+]/g, ''));
              if (!isNaN(pctValue)) {
                if (pctValue > 0) {
                  worksheet[cellRef].s.font.color = { rgb: '059669' };
                } else if (pctValue < 0) {
                  worksheet[cellRef].s.font.color = { rgb: 'DC2626' };
                }
                if (Math.abs(pctValue) > 5) {
                  worksheet[cellRef].s.font.bold = true;
                }
              }
              break;

            case 'status':
              worksheet[cellRef].s.alignment.horizontal = 'center';
              worksheet[cellRef].s.font.bold = true;

              const statusValue = String(cellValue).toLowerCase();
              if (statusValue.includes('good') || statusValue.includes('excellent')) {
                worksheet[cellRef].s.font.color = { rgb: 'FFFFFF' };
                worksheet[cellRef].s.fill = { fgColor: { rgb: '10B981' }, type: 'solid' };
              } else if (statusValue.includes('warning') || statusValue.includes('watch')) {
                worksheet[cellRef].s.font.color = { rgb: '000000' };
                worksheet[cellRef].s.fill = { fgColor: { rgb: 'F59E0B' }, type: 'solid' };
              } else if (statusValue.includes('urgent') || statusValue.includes('critical')) {
                worksheet[cellRef].s.font.color = { rgb: 'FFFFFF' };
                worksheet[cellRef].s.fill = { fgColor: { rgb: 'EF4444' }, type: 'solid' };
              }
              break;
          }
        }
      }
    }

    // Generate a meaningful worksheet name for the fallback function
    let fallbackWorksheetName = 'Data Analysis';

    if (jsonData && jsonData.title) {
      fallbackWorksheetName = jsonData.title
        .replace(/[^\w\s]/g, '')
        .replace(/\s+/g, ' ')
        .trim()
        .substring(0, 31);
    } else if (enhancedData.length > 0 && enhancedData[0].length > 0) {
      const firstHeader = enhancedData[0][0];
      if (firstHeader && typeof firstHeader === 'string' && firstHeader.length > 3) {
        fallbackWorksheetName = firstHeader
          .replace(/[^\w\s]/g, '')
          .replace(/\s+/g, ' ')
          .trim()
          .substring(0, 31);
      }
    }

    if (!fallbackWorksheetName || fallbackWorksheetName.length < 2) {
      fallbackWorksheetName = 'Data Analysis';
    }

    XLSX.utils.book_append_sheet(workbook, worksheet, fallbackWorksheetName);
  }

  // Convert to binary Excel format and download
  const excelBuffer = XLSX.write(workbook, {
    bookType: 'xlsx',
    type: 'array',
    bookSST: false,
    compression: true
  });

  const blob = new Blob([excelBuffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
  });

  downloadBlob(blob, `${fileName}.xlsx`);
  return 'Professional Excel file downloaded (basic styling)!';
};

// Helper function to download blob
const downloadBlob = (blob, filename) => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};